import { useFormikContext } from 'formik';
import { useRef, useState } from 'react';
import callApi from '../../utils/callApi';
import { Address } from '../../utils/checkoutState';
import { config } from '../../utils/config';
import { getInitialAddressValues, splitStreetHouseNumber } from '../../utils/streetHouseNumber';
import Modal from '../atoms/Modal';
import AddressList from '../molecules/AddressList';

export type AddressSuggestion = {
  city: string;
  streetHouseNumber: string;
  zipCode: string;
};

export const useAddressSuggestions = () => {
  const shouldSkipSuggestions = useRef(false);
  const [addressSuggestions, setAddressSuggestions] = useState<AddressSuggestion[]>([]);
  const [isAddressSuggestionsOpen, setAddressSuggestionsOpen] = useState(false);

  const suggestAddresses = async (addressValues: any) => {
    if (!config.features.addressSuggestions.enabled || shouldSkipSuggestions.current) {
      return undefined;
    }

    const _addressSuggestions = await getAddressSuggestions(addressValues);

    const addressMatchesSuggestion = _addressSuggestions.some(
      address =>
        address.streetHouseNumber === addressValues.streetHouseNumber &&
        address.city === addressValues.city &&
        address.zipCode === addressValues.zipCode
    );

    if (!_addressSuggestions.length || addressMatchesSuggestion) {
      return undefined;
    }

    setAddressSuggestions(_addressSuggestions);
    setAddressSuggestionsOpen(true);

    return _addressSuggestions;
  };

  const skipSuggestions = () => {
    shouldSkipSuggestions.current = true;
  };

  return { isAddressSuggestionsOpen, setAddressSuggestionsOpen, suggestAddresses, addressSuggestions, skipSuggestions };
};

export const formatAddressSuggestions = (addressSuggestions: AddressSuggestion[], addressValues: any): Address[] =>
  addressSuggestions.map((address, index) => {
    const { street, houseNumber } = splitStreetHouseNumber(address.streetHouseNumber, addressValues.country);
    const { zipCode, city } = address;

    const { country, firstName, lastName, gender, additional } = addressValues;

    return {
      id: index,
      hash: index.toString(),
      country: { iso2Code: country },
      salutationCode: gender as Address['salutationCode'],
      firstName,
      lastName,
      street,
      houseNumber,
      additional,
      zipCode,
      city,
    };
  });

export const getAddressSuggestions = async (addressValues: any): Promise<AddressSuggestion[]> =>
  callApi<AddressSuggestion[]>({
    endpoint: `/address/validate?${new URLSearchParams({
      city: addressValues.city,
      countryCode: addressValues.country,
      streetHouseNumber: addressValues.streetHouseNumber,
      zipCode: addressValues.zipCode,
    }).toString()}`,
    flow: 'legacy',
  });

type AddressSuggestionProps = {
  addresses: AddressSuggestion[];
  context: 'shippingAddress' | 'billingAddress';
  onClose: () => void;
  skipSuggestions: () => void;
  onSubmit?: () => void;
};

const AddressSuggestions = ({ addresses, context, onClose, skipSuggestions, onSubmit }: AddressSuggestionProps) => {
  const { values, setFieldValue, submitForm } = useFormikContext<any>();
  return (
    <Modal>
      <AddressList
        addresses={formatAddressSuggestions([...addresses, values[context]], values[context])}
        context={context}
        usage="suggestion"
        onCancel={onClose}
        onSelect={(address: Address) => {
          onClose();
          setFieldValue(context, { ...values[context], ...getInitialAddressValues(address) });
          skipSuggestions();
          if (onSubmit) {
            onSubmit();
          } else {
            submitForm();
          }
        }}
      />
    </Modal>
  );
};

export default AddressSuggestions;
